/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Empty, Modal, Input, Spin, Table, Button, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Text } from 'atoms';
import VehicleReplacementCardV2 from './VehicleReplacementCardV2';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';
import {
	searchPendingApprovals,
	approveVehicleReplacement,
	rejectVehicleReplacement,
} from 'store/approvalSliceV2';

const PageContainer = styled(Box)`
	max-width: 1600px;
	margin: 0 auto;
	padding: 24px;
`;

const HeaderContainer = styled(Box)`
	margin-bottom: 24px;
`;

const PageTitle = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #333;
	display: block;
	margin-bottom: 4px;
`;

const PageSubtitle = styled(Text)`
	font-size: 14px;
	color: #707070;
	display: block;
`;

const TabsContainer = styled(Box)`
	margin-top: 24px;
`;

const StyledTabs = styled(Tabs)`
	.ant-tabs-tab {
		font-size: 16px;
		font-weight: 500;
		padding: 12px 24px;
	}

	.ant-tabs-tab-active {
		.ant-tabs-tab-btn {
			color: #1890ff;
			font-weight: 600;
		}
	}

	.ant-tabs-ink-bar {
		background: #1890ff;
	}
`;

const ContentContainer = styled(Box)`
	margin-top: 24px;
`;

const EmptyStateContainer = styled(Box)`
	padding: 60px 20px;
	text-align: center;
`;

const LoadingContainer = styled(Box)`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 60px 20px;
`;

const StyledTable = styled(Table)`
	.ant-table-thead > tr > th {
		background-color: #18405d;
		color: #fff;
		font-weight: 600;
	}

	.ant-table-tbody > tr:nth-child(even) {
		background-color: #f5f5f5;
	}

	.ant-table-tbody > tr:hover {
		background-color: #e6f7ff;
	}
`;

const ApproveModalContent = styled(Box)`
	text-align: center;
`;

const ModalTitle = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #000;
	display: block;
	margin-bottom: 16px;
`;

const ModalMessage = styled(Text)`
	font-size: 14px;
	color: #666;
	display: block;
	margin-bottom: 24px;
	line-height: 1.5;
`;

const ModalButtonGroup = styled(Box)`
	display: flex;
	justify-content: center;
	gap: 12px;
	margin-top: 24px;
`;

const CancelButton = styled(Button)`
	border-color: #1890ff;
	color: #1890ff;
	background: #fff;
	min-width: 100px;

	&:hover {
		border-color: #40a9ff;
		color: #40a9ff;
		background: #fff;
	}
`;

const ApproveButtonStyled = styled(Button)`
	background: #1890ff;
	border-color: #1890ff;
	color: #fff;
	min-width: 100px;

	&:hover {
		background: #40a9ff;
		border-color: #40a9ff;
	}
`;

const { TextArea } = Input;

export default function VehicleReplacementApprovalPageV2() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('pending');
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [selectedApproval, setSelectedApproval] = useState(null);
	const [comments, setComments] = useState('');
	const [rejectionReason, setRejectionReason] = useState('');
	const [filePreviewVisible, setFilePreviewVisible] = useState(false);
	const [previewFileKey, setPreviewFileKey] = useState(null);
	const [previewFileName, setPreviewFileName] = useState('');

	const { approvals, loading, actionLoading } = useSelector((state) => state.approvalStateV2);

	const fetchApprovals = useCallback(
		(status) => {
			dispatch(
				searchPendingApprovals({
					filter_by: {
						approval_type: 'vehicle_replacement',
						status: status === 'pending' ? 'pending' : status,
					},
					pagination: { page: 1, per_page: 20 },
				})
			);
		},
		[dispatch]
	);

	useEffect(() => {
		fetchApprovals(activeTab);
	}, [activeTab, fetchApprovals]);

	const handleTabChange = (key) => {
		setActiveTab(key);
	};

	const handleView = (id, approvalId) => {
		// Navigate to approval details page with approval ID
		if (approvalId) {
			navigate(`/approvals/vehicle-replacement/${approvalId}`, {
				state: { fromApproval: true, approvalId, entityId: id },
			});
		} else {
			// Fallback to vehicle replacement route if no approval ID
			navigate(`/vehicle-replacement/${id}`, {
				state: { fromApproval: true, approvalId },
			});
		}
	};

	const handleApproveClick = (approval) => {
		setSelectedApproval(approval);
		setComments('');
		setApproveModalVisible(true);
	};

	const handleRejectClick = (approval) => {
		setSelectedApproval(approval);
		setRejectionReason('');
		setRejectModalVisible(true);
	};

	const handleViewDocument = (fileKey, fileName = '') => {
		if (!fileKey) return;
		setPreviewFileKey(fileKey);
		setPreviewFileName(fileName);
		setFilePreviewVisible(true);
	};

	const handleFilePreviewClose = () => {
		setFilePreviewVisible(false);
		setPreviewFileKey(null);
		setPreviewFileName('');
	};

	const handleApproveConfirm = async () => {
		if (!selectedApproval) return;

		if (!comments.trim()) {
			message.error('Comments are required for approval');
			return;
		}

		// Use approval_id from the approval object
		const approvalId = selectedApproval.id;
		const result = await dispatch(
			approveVehicleReplacement({
				approval_id: approvalId,
				comments,
			})
		);
		if (result.success) {
			setApproveModalVisible(false);
			setSelectedApproval(null);
			setComments('');
			// Refresh the list
			fetchApprovals(activeTab);
		}
	};

	const handleRejectConfirm = async () => {
		if (!selectedApproval || !rejectionReason.trim()) {
			return;
		}

		// Use approval_id from the approval object
		const approvalId = selectedApproval.id;
		const result = await dispatch(
			rejectVehicleReplacement({
				approval_id: approvalId,
				rejection_reason: rejectionReason,
			})
		);
		if (result.success) {
			setRejectModalVisible(false);
			setSelectedApproval(null);
			setRejectionReason('');
			// Refresh the list
			fetchApprovals(activeTab);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		return moment(dateString).format('DD-MM-YYYY');
	};

	const formatTime = (dateString) => {
		if (!dateString) return '-';
		return moment(dateString).format('HH:mm');
	};

	const getApprovedColumns = () => [
		{
			title: 'Approved at',
			key: 'approved_at',
			width: 180,
			render: (_, record) =>
				!record.approved_at ? (
					'-'
				) : (
					<>
						<div>{formatDate(record.approved_at)}</div>
						<div style={{ fontSize: '12px', color: '#666' }}>
							{formatTime(record.approved_at)}
						</div>
					</>
				),
		},
		{
			title: 'Approved by',
			dataIndex: 'approved_by',
			key: 'approved_by',
			width: 150,
		},
	];

	const getRejectedColumns = () => [
		{
			title: 'Rejected at',
			key: 'rejected_at',
			width: 180,
			render: (_, record) =>
				!record.rejected_at && !record.approved_at ? (
					'-'
				) : (
					<>
						<div>{formatDate(record.rejected_at || record.approved_at)}</div>
						<div style={{ fontSize: '12px', color: '#666' }}>
							{formatTime(record.rejected_at || record.approved_at)}
						</div>
					</>
				),
		},
		{
			title: 'Rejected by',
			dataIndex: 'rejected_by',
			key: 'rejected_by',
			width: 150,
			render: (_, record) => record.rejected_by || record.approved_by || '-',
		},
	];

	const getActionColumn = () => ({
		title: 'Action',
		key: 'action',
		width: 100,
		fixed: 'right',
		render: (_, record) => (
			<Button
				type="text"
				icon={<EyeOutlined />}
				onClick={() => handleView(record.entity_id || record.id, record.approval_id)}
				title="View Details"
			/>
		),
	});

	const getTableColumns = (tabKey) => {
		const baseColumns = [
			{
				title: 'Vehicle Number',
				dataIndex: 'vehicle_number',
				key: 'vehicle_number',
				width: 180,
			},
			{
				title: 'Vendor Name',
				dataIndex: 'vendor_name',
				key: 'vendor_name',
				width: 180,
			},
			{
				title: 'Vehicle Type',
				dataIndex: 'vehicle_type',
				key: 'vehicle_type',
				width: 150,
			},
			{
				title: 'RC Document',
				key: 'rc_document',
				width: 200,
				render: (_, record) =>
					record.rc_document_url || record.rc_document_name ? (
						<span
							role="button"
							tabIndex={0}
							style={{ color: '#1890ff', cursor: 'pointer' }}
							onClick={() =>
								handleViewDocument(record.rc_document_url, 'RC Document')
							}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									handleViewDocument(record.rc_document_url, 'RC Document');
								}
							}}
						>
							{record.rc_document_name || 'View RC Document'}
						</span>
					) : (
						'-'
					),
			},
			{
				title: 'Insurance Document',
				key: 'insurance_document',
				width: 200,
				render: (_, record) =>
					record.insurance_document_url || record.insurance_document_name ? (
						<span
							role="button"
							tabIndex={0}
							style={{ color: '#1890ff', cursor: 'pointer' }}
							onClick={() =>
								handleViewDocument(
									record.insurance_document_url,
									'Insurance Document'
								)
							}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									handleViewDocument(
										record.insurance_document_url,
										'Insurance Document'
									);
								}
							}}
						>
							{record.insurance_document_name || 'View Insurance Document'}
						</span>
					) : (
						'-'
					),
			},
			{
				title: 'City',
				dataIndex: 'city',
				key: 'city',
				width: 150,
			},
			{
				title: 'Created by',
				dataIndex: 'created_by',
				key: 'created_by',
				width: 150,
			},
		];

		// Add approval columns for approved/rejected tabs
		if (tabKey === 'approved') {
			baseColumns.push(...getApprovedColumns());
		} else if (tabKey === 'rejected') {
			baseColumns.push(...getRejectedColumns());
		} else {
			// Add view action column only for pending tab
			baseColumns.push(getActionColumn());
		}

		return baseColumns;
	};

	// Helper functions to extract values from approval object
	const getVehicleNumber = (approval) =>
		approval.entity_data?.vehicle_number ||
		approval.entity_data?.new_vehicle_number ||
		approval.entity_summary?.vehicle_number ||
		'-';

	const getVendorName = (approval) =>
		approval.entity_summary?.vendor_name ||
		approval.entity_data?.vendor_name ||
		approval.related_data?.vendor?.name ||
		'-';

	const getVehicleType = (approval) =>
		approval.entity_summary?.vehicle_type ||
		approval.entity_data?.vehicle_type ||
		approval.entity_data?.vehicle_type_name ||
		approval.related_data?.vehicle_type?.name ||
		'-';

	const getRCDocumentUrl = (approval) =>
		approval.entity_summary?.rc_document_url ||
		approval.entity_data?.rc_document_url ||
		approval.entity_data?.rc_document?.url ||
		approval.entity_data?.documents?.rc?.url ||
		null;

	const getRCDocumentName = (approval) =>
		approval.entity_summary?.rc_document_name ||
		approval.entity_data?.rc_document_name ||
		approval.entity_data?.rc_document?.name ||
		approval.entity_data?.documents?.rc?.name ||
		null;

	const getInsuranceDocumentUrl = (approval) =>
		approval.entity_summary?.insurance_document_url ||
		approval.entity_data?.insurance_document_url ||
		approval.entity_data?.insurance_document?.url ||
		approval.entity_data?.documents?.insurance?.url ||
		null;

	const getInsuranceDocumentName = (approval) =>
		approval.entity_summary?.insurance_document_name ||
		approval.entity_data?.insurance_document_name ||
		approval.entity_data?.insurance_document?.name ||
		approval.entity_data?.documents?.insurance?.name ||
		null;

	const getCity = (approval) =>
		approval.entity_summary?.city ||
		approval.entity_data?.city ||
		approval.entity_data?.vehicle_city ||
		approval.related_data?.city ||
		'-';

	const getApprovedBy = (approval) =>
		approval.approved_by_name ||
		approval.approver_name ||
		approval.related_data?.approver?.name ||
		(approval.approver_id ? `User ${approval.approver_id}` : '-');

	const getRejectedBy = (approval) =>
		approval.rejected_by_name ||
		approval.related_data?.rejecter?.name ||
		(approval.rejecter_id ? `User ${approval.rejecter_id}` : '-');

	const mapApprovalToTableData = (approval) => ({
		key: approval.id,
		id: approval.entity_id || approval.id,
		entity_id: approval.entity_id || approval.id,
		approval_id: approval.id, // Store approval_id for navigation
		vehicle_number: getVehicleNumber(approval),
		vendor_name: getVendorName(approval),
		vehicle_type: getVehicleType(approval),
		rc_document_url: getRCDocumentUrl(approval),
		rc_document_name: getRCDocumentName(approval),
		insurance_document_url: getInsuranceDocumentUrl(approval),
		insurance_document_name: getInsuranceDocumentName(approval),
		city: getCity(approval),
		created_by: approval.requested_by_name || '-',
		approved_at: approval.approved_at || null,
		approved_by: getApprovedBy(approval),
		rejected_at: approval.rejected_at || null,
		rejected_by: getRejectedBy(approval),
	});

	const renderTableContent = (tabKey) => {
		const tableData = approvals.map(mapApprovalToTableData);
		const columns = getTableColumns(tabKey);

		return (
			<ContentContainer>
				<StyledTable
					columns={columns}
					dataSource={tableData}
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) => `Total ${total} items`,
						position: ['bottomRight'],
					}}
					scroll={{ x: 'max-content' }}
					locale={{
						emptyText: (
							<Empty
								description={
									<Text fontSize="14px" color="#999">
										No {tabKey} vehicle replacement requests found
									</Text>
								}
							/>
						),
					}}
				/>
			</ContentContainer>
		);
	};

	const renderContent = (tabKey) => {
		if (loading) {
			return (
				<LoadingContainer>
					<Spin size="large" tip="Loading approvals..." />
				</LoadingContainer>
			);
		}

		if (!approvals || approvals.length === 0) {
			return (
				<EmptyStateContainer>
					<Empty
						description={
							<Text fontSize="14px" color="#999">
								No {tabKey} vehicle replacement requests found
							</Text>
						}
					/>
				</EmptyStateContainer>
			);
		}

		// Show table for approved/rejected tabs, cards for pending
		if (tabKey === 'approved' || tabKey === 'rejected') {
			return renderTableContent(tabKey);
		}

		// Show cards for pending tab
		return (
			<ContentContainer>
				{approvals.map((approval) => {
					// Map approval data to vehicle replacement format
					const replacement = {
						id: approval.entity_id || approval.id,
						approval_id: approval.id,
						vehicle_number: getVehicleNumber(approval),
						vendor_name: getVendorName(approval),
						vehicle_type: getVehicleType(approval),
						rc_document_url: getRCDocumentUrl(approval),
						rc_document_name: getRCDocumentName(approval),
						insurance_document_url: getInsuranceDocumentUrl(approval),
						insurance_document_name: getInsuranceDocumentName(approval),
						city: getCity(approval),
						created_by: approval.requested_by_name || '-',
					};

					return (
						<VehicleReplacementCardV2
							key={approval.id}
							replacement={replacement}
							onView={() =>
								handleView(approval.entity_id || approval.id, approval.id)
							}
							onApprove={() => handleApproveClick(approval)}
							onReject={() => handleRejectClick(approval)}
						/>
					);
				})}
			</ContentContainer>
		);
	};

	const tabItems = [
		{
			key: 'pending',
			label: 'Pending',
			children: renderContent('pending'),
		},
		{
			key: 'approved',
			label: 'Approved',
			children: renderContent('approved'),
		},
		{
			key: 'rejected',
			label: 'Rejected',
			children: renderContent('rejected'),
		},
	];

	return (
		<PageContainer>
			<HeaderContainer>
				<PageTitle>Vehicle Replacement request approvals</PageTitle>
				<PageSubtitle>Approve vehicle replacement requests</PageSubtitle>
			</HeaderContainer>

			<TabsContainer>
				<StyledTabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
			</TabsContainer>

			{/* Approve Modal */}
			<Modal
				open={approveModalVisible}
				onCancel={() => {
					setApproveModalVisible(false);
					setSelectedApproval(null);
					setComments('');
				}}
				footer={null}
				width={500}
				centered
			>
				<ApproveModalContent>
					<ModalTitle>Approve Vehicle Replacement</ModalTitle>
					<ModalMessage>
						This vehicle replacement will be approved and you won&apos;t be able to
						revert this change.
					</ModalMessage>
					<Box textAlign="left" mb={3}>
						<Text
							fontSize="14px"
							color="#333"
							mb={1}
							display="block"
							style={{ textAlign: 'left' }}
						>
							Comments <span style={{ color: '#ff4d4f' }}>*</span>
						</Text>
						<TextArea
							rows={4}
							value={comments}
							onChange={(e) => setComments(e.target.value)}
							placeholder="Enter comments for approval"
							maxLength={500}
							showCount
						/>
					</Box>
					<ModalButtonGroup>
						<CancelButton
							onClick={() => {
								setApproveModalVisible(false);
								setSelectedApproval(null);
								setComments('');
							}}
						>
							Cancel
						</CancelButton>
						<ApproveButtonStyled
							onClick={handleApproveConfirm}
							loading={actionLoading}
							disabled={!comments.trim()}
						>
							Approve
						</ApproveButtonStyled>
					</ModalButtonGroup>
				</ApproveModalContent>
			</Modal>

			{/* Reject Modal */}
			<Modal
				open={rejectModalVisible}
				onCancel={() => {
					setRejectModalVisible(false);
					setSelectedApproval(null);
					setRejectionReason('');
				}}
				footer={null}
				width={500}
				centered
			>
				<ApproveModalContent>
					<ModalTitle>Reject Vehicle Replacement</ModalTitle>
					<ModalMessage>
						This vehicle replacement will be rejected and you won&apos;t be able to
						revert this change.
					</ModalMessage>
					<Box textAlign="left" mb={3}>
						<Text
							fontSize="14px"
							color="#333"
							mb={1}
							display="block"
							style={{ textAlign: 'left' }}
						>
							Rejection Reason <span style={{ color: '#ff4d4f' }}>*</span>
						</Text>
						<TextArea
							rows={4}
							value={rejectionReason}
							onChange={(e) => setRejectionReason(e.target.value)}
							placeholder="Enter rejection reason"
							maxLength={500}
							showCount
						/>
					</Box>
					<ModalButtonGroup>
						<CancelButton
							onClick={() => {
								setRejectModalVisible(false);
								setSelectedApproval(null);
								setRejectionReason('');
							}}
						>
							Cancel
						</CancelButton>
						<ApproveButtonStyled
							onClick={handleRejectConfirm}
							loading={actionLoading}
							disabled={!rejectionReason.trim()}
							danger
						>
							Reject
						</ApproveButtonStyled>
					</ModalButtonGroup>
				</ApproveModalContent>
			</Modal>

			<FilePreviewModal
				visible={filePreviewVisible}
				fileKey={previewFileKey}
				fileName={previewFileName}
				entity="vehicle"
				onClose={handleFilePreviewClose}
			/>
		</PageContainer>
	);
}
